import { useMemo } from 'react';
import { usePatientAnamnesis } from '@/hooks/use-patient-anamnesis';
import type { ResolvedSource } from '@/components/wizard/ai-data-sources';

/**
 * Answers whether a settled `document` source is actually on file.
 *
 * The requirements endpoint says a section no longer needs the anamnesis, which
 * is not the same question: on História Clínica a source goes `available` once
 * the derived history is ready, and that survives the anamnesis being deleted.
 * The document itself lives on the patient, so it takes this lookup to tell
 * "nothing is in the way" from "the file is there".
 *
 * Called from `AIGeneratorCard` rather than from each step, the same way the
 * form actions used to be, so a step never has to know that the anamnesis has a
 * shape. The query only runs when a section actually declares a document
 * source, so Diagnóstico and Recomendações never fetch an anamnesis they do not
 * render.
 */
export function useDocumentSourcePresence(
    resolvedSources: ResolvedSource[],
    patientId: string | undefined,
): ResolvedSource[] {
    const hasAnamnesisSource = resolvedSources.some(
        (source) => source.id === 'anamnesis' && source.kind === 'document',
    );

    const { data: anamnesis, isSuccess } = usePatientAnamnesis(
        hasAnamnesisSource ? patientId : undefined,
    );

    return useMemo(() => {
        if (!isSuccess) return resolvedSources;

        return resolvedSources.map((source) =>
            source.id === 'anamnesis' && source.kind === 'document'
                ? { ...source, present: Boolean(anamnesis) }
                : source,
        );
    }, [resolvedSources, anamnesis, isSuccess]);
}
